import React from 'react';
import { language } from '../../services/LanguageService';
import { SubManager } from '../../classes/SubManager';
import { Subject, catchError, map, mergeMap, of, throwError } from 'rxjs';
import { Axios } from '../../classes/Axios';
import { AsperaError } from '../../classes/AsperaError';
import { deleteSensitiveInfo, getEnvironmentVar, isValidEmail } from '../../helpers/helpers';
import { modal } from '../../services/ModalService';
import { AsperaWrapper } from '../AsperaWrapper';
import { AsperaForm } from '../AsperaForm';
import { getSupportData } from '../../helpers/starterHelpers';
import { notification } from '../../services/NotificationService';
/**
 * Make the feedback submit to the server handling it.
 *
 * @param data - Data from form or external forms
 *
 * @returns Observable with info on the API call
 */
export const sendFeedback = (data) => {
    const url = getEnvironmentVar('feedback_url');
    const extraData = {
        data: getSupportData(),
        appData: data.extraData,
    };
    const postData = {
        product: data.productName,
        feedback: data.feedback,
        name: data.name,
        email: data.email,
        data: '',
    };
    if (data.includeData) {
        try {
            postData.data = JSON.stringify(extraData, undefined, 2);
        }
        catch (error) {
            new AsperaError('sendFeedback: unable to stringify JSON', error);
        }
    }
    postData.data = deleteSensitiveInfo(postData.data);
    if (url) {
        return Axios.post(url, postData);
    }
    return throwError(() => new AsperaError('AsperaFeedback: no feedback URL present'));
};
/**
 * React Component
 *
 * The AsperaFeedback provides the modal that allows submitting feedback.
 *
 * @example
 * ```javascript
 * <AsperaFeedback extraData={data} />
 * ```
 */
export class AsperaFeedback extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
        };
        this.model = { includeData: true };
        this.validFormSubject = new Subject();
        this.subManager = new SubManager();
    }
    get formData() {
        return {
            id: 'aspera-feedback-form',
            model: this.model,
            items: [
                {
                    type: 'text',
                    label: language.get('feedbackModal.name'),
                    pointer: 'name',
                },
                {
                    type: 'text',
                    label: language.get('feedbackModal.email'),
                    pointer: 'email',
                    errorText: language.get('feedbackModal.emailIsInvalid'),
                    validationCheck: data => isValidEmail(data),
                },
                {
                    type: 'textarea',
                    label: language.get('feedbackModal.feedback'),
                    pointer: 'feedback',
                    required: true,
                },
                {
                    type: 'toggle',
                    label: language.get('feedbackModal.includeDebuggingData'),
                    pointer: 'includeData',
                },
            ],
        };
    }
    componentDidMount() {
        const { disableSubject, initialMessage } = this.props;
        this.model.feedback = initialMessage;
        this.subManager.setSubscription(this.validFormSubject.subscribe({
            next: valid => {
                if (disableSubject) {
                    disableSubject.next(!valid);
                }
            },
            error: error => {
                new AsperaError('AsperaFeedback: unable to subscribe for valid form', error);
            },
        }));
        this.setState({ loading: false });
    }
    componentWillUnmount() {
        const { modalClose } = this.props;
        this.subManager.unsubscribeAll();
        if (typeof modalClose === 'function') {
            modalClose(this.model);
        }
    }
    render() {
        const { className, productName, extraData } = this.props;
        const { loading } = this.state;
        if (!this.model.productName) {
            this.model.productName = productName;
        }
        if (!this.model.extraData && extraData) {
            this.model.extraData = extraData;
        }
        return (React.createElement(AsperaWrapper, { loading: loading, className: `aspera-feedback ${className}`, disableWrapping: true },
            React.createElement(AsperaForm, { formDetails: this.formData, validFormSubject: this.validFormSubject })));
    }
}
export const renderFeedbackModal = (modalFeedbackProps, overrideTitle, overrideLabel, showSuccessNotification) => {
    const modalInfo = modal.prepare();
    const disableSubject = new Subject();
    const modalProps = {
        modalHeading: overrideTitle || language.get('feedbackModal.title'),
        modalLabel: overrideLabel,
        primaryButtonText: language.get('feedbackModal.send'),
    };
    return modal.create(modalInfo.id, modalProps, React.createElement(AsperaFeedback, Object.assign({}, modalFeedbackProps, { disableSubject: disableSubject, modalClose: modalInfo.callback })), disableSubject.asObservable()).pipe(mergeMap(data => {
        return sendFeedback(data.data).pipe(map(data => {
            if (showSuccessNotification) {
                notification.create('success', language.get('feedbackModal.feedbackSubmitted'));
            }
            return data;
        }), catchError(error => {
            notification.create('error', language.get('feedbackModal.feedbackUnable'));
            new AsperaError('renderFeedbackModal: unable to submit feedback', error, true);
            return of({});
        }));
    }));
};
